<template>
    <div :class="classes">
        <div class="value-box">{{ sliderValue + suffix }}</div>
        <div
            class="slider-container"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
        >
            <Slider
                v-model="sliderValue"
                :step="step"
                :min="min"
                :max="max"
                :orientation="orientation"
                :disabled="disabled"
                :name="name"
            />
            <div v-if="tickMarks" class="tick-marks">
                <div v-for="i in tickSections + 1" :key="i" :class="tickClasses(i)"></div>
                <div class="tick-marks--mark tick-marks--min">
                    {{ min }}
                </div>
                <div class="tick-marks--mark tick-marks--max">
                    {{ max }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Slider from 'primevue/slider'
import InputText from 'primevue/inputtext'

export default {
    name: 'CoreSlider',

    components: { InputText, Slider },

    props: {
        modelValue: {
            type: [Number, String],
            default: 0,
        },
        name: {
            type: String,
            default: '',
        },
        step: {
            type: Number,
            default: 1,
        },
        min: {
            type: Number,
            default: 0,
        },
        max: {
            type: Number,
            default: 100,
        },
        orientation: {
            type: String,
            default: 'horizontal',
        },
        suffix: {
            type: String,
            default: '',
        },
        tickMarks: {
            type: Boolean,
            default: false,
        },
        tickSections: {
            type: Number,
            default: 10,
        },
        validator: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue'],

    data() {
        return {
            isMouseDown: false,
        }
    },

    mounted() {
        if (
            this.modelValue === undefined ||
            this.modelValue === null ||
            this.modelValue === '' ||
            isNaN(this.modelValue)
        ) {
            this.$emit('update:modelValue', 0)
        }

        if (this.validator) {
            if (Number(this.modelValue) === this.min) {
                this.validator.$reset()
            } else {
                this.validator.$touch()
            }
        }
    },

    computed: {
        sliderValue: {
            get() {
                const intVal = parseInt(this.modelValue)
                return intVal
            },
            set(newValue) {
                this.$emit('update:modelValue', newValue)

                if (this.validator) {
                    this.validator.$touch()
                }
            },
        },

        classes() {
            return {
                'core-slider': true,
                'core-valid': this.validator && !this.validator.$invalid && this.validator.$dirty,
                'core-invalid': this.validator && this.validator.$invalid && this.validator.$dirty,
                'core-warning': false,
                'p-disabled': this.disabled,
            }
        },

        validatorErrors() {
            if (this.validator?.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator?.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },
    },

    methods: {
        handleMouseDown(event) {
            this.isMouseDown = true
            this.updateSliderValue(event)
            document.addEventListener('mousemove', this.handleMouseMove)
            document.addEventListener('mouseup', this.handleMouseUp)
            document.body.classList.add('core-slider-grabbing')
        },
        handleMouseMove(event) {
            if (this.isMouseDown) {
                this.updateSliderValue(event)
            }
        },
        handleMouseUp() {
            this.isMouseDown = false
            document.removeEventListener('mousemove', this.handleMouseMove)
            document.removeEventListener('mouseup', this.handleMouseUp)
            document.body.classList.remove('core-slider-grabbing')
        },
        updateSliderValue(event) {
            const rect = this.$el.querySelector('.slider-container').getBoundingClientRect()
            let clickedValue =
                ((event.clientX - rect.left) / rect.width) * (this.max - this.min) + this.min
            clickedValue = Math.max(this.min, Math.min(this.max, clickedValue))
            this.sliderValue = Math.round(clickedValue / this.step) * this.step
        },
        tickClasses(id) {
            return {
                'tick-marks--tick': true,
                'tick-marks--tick--long':
                    id === 1 ||
                    id === this.tickSections + 1 ||
                    id === Math.ceil(this.tickSections / 2) + 1,
            }
        },
    },
}
</script>

<style>
.core-slider-grabbing {
    cursor: grabbing;
    user-select: none;
}

.core-slider-grabbing * {
    pointer-events: none;
}

.core-slider {
    position: relative;
    display: flex;
    align-items: center;

    .value-box {
        height: 4.8rem;
        width: 4.8rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--color-neutral-500);
        font-size: 1.4rem;
        border-radius: 4px;
        margin-right: 10px;
    }

    .slider-container {
        flex-grow: 1;
        position: relative;
        padding: var(--space-2);
        border: 1px solid var(--color-neutral-500);
        height: 4.8rem;
        border-radius: 4px;
        cursor: grab;

        .p-slider {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: none;
            height: 4.6rem;
            /* width: calc(100% - {2 * var(--space-2)}); */
        }
        .p-slider .p-slider-range {
            background: var(--color-primary);
            border-radius: 4px;
        }
        .p-slider-horizontal .p-slider-range {
            top: var(--space-2);
            right: var(--space-2);
            bottom: var(--space-2);
            left: var(--space-2);
            height: auto;
        }
        .p-slider .p-slider-handle {
            display: none;
        }

        .tick-marks {
            position: absolute;
            top: calc(100% - 25px);
            left: 0;
            right: 0;
            pointer-events: none;

            display: flex;
            justify-content: space-between;

            & .tick-marks--mark {
                position: absolute;
                top: 5px;
                font-weight: 600;
                font-size: 1.2rem;
                color: var(--color-secondary-200);
                background-color: var(--color-neutral-400);
                padding: 3px 6px;
                border-radius: 3px;
            }
            & .tick-marks--min {
                left: 0;
            }
            & .tick-marks--max {
                right: 0;
            }
        }
    }

    &.p-disabled {
        & > .value-box,
        & > .slider-container {
            background-color: var(--color-disabled-bg);
            color: var(--text-disabled);
        }
    }
    &.core-invalid {
        .value-box {
            border: 1px solid var(--color-error);
        }
        .slider-container {
            border: 1px solid var(--color-error);
        }
    }
    &.core-warning {
        .value-box {
            border: 1px solid var(--color-warning);
        }
        .slider-container {
            border: 1px solid var(--color-warning);
        }
    }
    &.core-valid {
        .value-box {
            border: 1px solid var(--color-primary);
        }
        .slider-container {
            border: 1px solid var(--color-primary);
        }
    }
}
</style>
